import React, { PropsWithChildren, useEffect, useState } from 'react';
import { Trans } from 'react-i18next';
import {
  ModalContainer,
  DialogBox,
  DialogPolicyBox,
  DialogBtnBox,
  DialogBtn,
  DialogBorder,
  DialogSpanText,
  PolicySpanText,
  DialogBtnCancel,
  ModalEmailInput,
  DialogDescBox,
  StickyHeader,
} from './Modal.style';

import { StyledDownloadBtnlTextCancel, StyledDownloadBtnlTextSubmit } from './ModalDownload.style';

import {
  ClearButton,
  ClearIcon,
  DialogCheckBox,
  DialogInput,
  DialogInputText,
  StyledDownloadPolicyLink,
  StyledUnderline,
} from '@pages/Main/Common/Purchase/Modal/Modal.style';
import { useLocation } from 'react-router-dom';
import { IconCheckBoxBlack, IconCheckBoxEmpty, IconCloseIcon, IconXMark } from '@assets/images/icon/';
import emailjs from 'emailjs-com';
import { RequestInputBox } from '../../Request/Request.style';

import Template from './Template';
import i18n from '@pages/i18n';
import { DivFlexRow, DivW100Padding, DivWidth100 } from '@pages/CommonStyle/commonDiv.style';
import { HeadingColor, ParagraphColor, Spacing, SpacingDTM } from '@pages/CommonStyle/common.style';
import { CloseBtn } from '@pages/CommonStyle/commonBtn';
import { Backdrop } from '@pages/Enterprise/Download/Modal/Modal.style';
import { StyledHeading } from '@components/Text';

interface ModalDefaultType {
  onClickToggleModal: () => void;
  content: string;
  onSubscribe: () => void;
  onSaveEmail: (email: string) => void;
  pageName: string;
}

function ModalDone({ onClickToggleModal, content, onSubscribe, onSaveEmail, pageName }: PropsWithChildren<ModalDefaultType>) {
  /* -------------------------------------------------------------------------- */
  /* locale & placeholder                                                      */
  /* -------------------------------------------------------------------------- */
  const location = useLocation();

  const locale = location.pathname.includes('/en/')
    ? 'en'
    : location.pathname.includes('/jp/')
    ? 'jp'
    : location.pathname.includes('/cn/')
    ? 'cn'
    : location.pathname.includes('/vi/')
    ? 'vi'
    : 'ko';

  const placeholders = {
    name: {
      en: 'Please enter your name',
      jp: 'お名前を入力してください',
      cn: '请输入您的姓名。',
      vi: 'Vui lòng nhập tên của bạn.',
      ko: '이름을 입력해 주세요.',
    },
    company: {
      en: 'Please enter your company',
      jp: '会社名を入力してください',
      cn: '请输入公司名称。',
      vi: 'Vui lòng nhập tên công ty.',
      ko: '회사명을 입력해 주세요.',
    },
    email: {
      en: 'Please enter an email',
      jp: 'メールを入力してください',
      cn: '请输入电子邮箱。',
      vi: 'Vui lòng nhập email của bạn.',
      ko: '이메일을 입력해 주세요.',
    },
  } as const;

  const labels = {
    name: {
      ko: '이름*',
      en: 'Name',
      jp: 'お名前',
      cn: '姓名',
      vi: 'Tên',
    },
    company: {
      ko: '회사*',
      en: 'Company',
      jp: '会社名',
      cn: '公司名称',
      vi: 'Công ty',
    },
    email: {
      ko: '업무용 이메일*',
      en: 'Work email',
      jp: 'メール',
      cn: '邮箱',
      vi: 'Email',
    },
  } as const;

  /* -------------------------------------------------------------------------- */
  /* state                                                                     */
  /* -------------------------------------------------------------------------- */
  const [templateID, setTemplateID] = useState<string>('');
  const [page, setPage] = useState<string>('');

  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [email, setEmail] = useState('');

  const [focus, setFocus] = useState({
    name: false,
    company: false,
    email: false,
  });

  const [checked, setChecked] = useState(false);
  const [submit, setSubmit] = useState(false);

  /* -------------------------------------------------------------------------- */
  /* handlers                                                                  */
  /* -------------------------------------------------------------------------- */
  const handleFocus = (field: 'name' | 'company' | 'email') => {
    setFocus({ name: false, company: false, email: false, [field]: true });
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, field: 'name' | 'company' | 'email') => {
    if (e.key !== 'Tab') return;
    e.preventDefault();
    const fields: ('name' | 'company' | 'email')[] = ['name', 'company', 'email'];
    const curIdx = fields.indexOf(field);
    const nextField = fields[curIdx % fields.length];
    const nextEl = document.getElementById(`${nextField}-input`);
    if (nextEl) (nextEl as HTMLInputElement).focus();
  };

  /* ────────────────────────────────────────────────────────────────────────── */
  /* form validation                                                           */
  /* ────────────────────────────────────────────────────────────────────────── */
  useEffect(() => {
    const isEmailValid = email.includes('@') && email.includes('.');
    const isFormValid = name.trim() !== '' && company.trim() !== '' && isEmailValid && checked;

    setSubmit(isFormValid);
  }, [name, company, email, checked]);

  /* -------------------------------------------------------------------------- */
  /* emailJS                                                                   */
  /* -------------------------------------------------------------------------- */
  const onSendEmail = () => {
    emailjs.init('SoQtE3CThBY_wpeDn');
    const mailForm = { name, company, email };

    emailjs
      .send('service_7yv5b7d', templateID, mailForm)
      .then((res) => console.log('메일 발송 성공:', res))
      .catch((err) => console.error('메일 발송 실패:', err));
  };

  const onClickEmail = () => {
    emailjs.init('SoQtE3CThBY_wpeDn');
    const mailForm = {
      name,
      company,
      email,
      page,
      lang: i18n.language,
    };

    emailjs
      .send('service_7yv5b7d', 'template_bhg67ab', mailForm)
      .then((res) => console.log('클릭 알림 메일 성공:', res))
      .catch((err) => console.error('클릭 알림 메일 실패:', err));
  };

  /* -------------------------------------------------------------------------- */
  /* submit                                                                    */
  /* -------------------------------------------------------------------------- */
  const onDownload = () => {
    if (!submit) return;

    onSendEmail(); // 실제 자료 전송 메일
    onClickEmail(); // 클릭 알림용 메일
    onSubscribe(); // 다음 모달
    onSaveEmail(email); // 상위 컴포넌트에 이메일 전달
    onClickToggleModal();
  };

  /* -------------------------------------------------------------------------- */
  /* clear buttons                                                             */
  /* -------------------------------------------------------------------------- */
  const clearField = (setter: React.Dispatch<React.SetStateAction<string>>) => setter('');

  /* -------------------------------------------------------------------------- */
  /* render                                                                    */
  /* -------------------------------------------------------------------------- */
  return (
    <ModalContainer>
      <DialogBox noPadding={true}>
        <Template pageName={pageName} setPageName={setPage} templateID={setTemplateID} />

        <DivW100Padding a={0} b={25} c={25} d={25}>
          <StickyHeader>
            {/* ───────── Header ───────── */}
            <DivFlexRow>
              <StyledHeading size="small">
                <Trans i18nKey="solutionCommon.modal.download.title" />
              </StyledHeading>
              <Spacing pixel="8" />
              <CloseBtn src={IconXMark} onClick={onClickToggleModal} size="20" alt="close mark" />
            </DivFlexRow>

            <DialogBorder />
          </StickyHeader>
          <Spacing pixel="32" />

          {/* ───────── Description ───────── */}
          <HeadingColor size="xxxsmall" color="#646464">
            <Trans i18nKey="solutionCommon.modal.desc" />
          </HeadingColor>
          <Spacing pixel="8" />
          <ParagraphColor size="medium" color="#646464">
            <Trans i18nKey="solutionCommon.modal.subDesc" />
          </ParagraphColor>

          <Spacing pixel="24" />

          <DialogDescBox>
            <HeadingColor size="xxxsmall" color="#646464">
              <Trans i18nKey="solutionCommon.modal.descBox.title" />
            </HeadingColor>
            <Spacing pixel="8" />
            <ParagraphColor size="medium" color="#646464">
              <Trans i18nKey="solutionCommon.modal.descBox.desc" />
            </ParagraphColor>
          </DialogDescBox>

          <Spacing pixel="24" />

          {/* ───────── Name ───────── */}
          <RequestInputBox inputValue={focus.name}>
            <DialogInputText inputValue={focus.name}>{labels.name[locale]}</DialogInputText>
            <ModalEmailInput focus={focus.name}>
              <DialogInput
                id="name-input"
                value={name}
                placeholder={placeholders.name[locale]}
                onChange={(e) => setName(e.target.value)}
                onClick={() => handleFocus('name')}
                onKeyDown={(e) => handleKeyDown(e, 'name')}
              />
              {name && focus.name && (
                <ClearButton onClick={() => clearField(setName)}>
                  <ClearIcon src={IconCloseIcon} alt="clear name" />
                </ClearButton>
              )}
            </ModalEmailInput>
          </RequestInputBox>

          <Spacing pixel="8" />

          {/* ───────── Company ───────── */}
          <RequestInputBox inputValue={focus.company}>
            <DialogInputText inputValue={focus.company}> {labels.company[locale]}</DialogInputText>
            <ModalEmailInput focus={focus.company}>
              <DialogInput
                id="company-input"
                value={company}
                placeholder={placeholders.company[locale]}
                onChange={(e) => setCompany(e.target.value)}
                onClick={() => handleFocus('company')}
                onKeyDown={(e) => handleKeyDown(e, 'company')}
              />
              {company && focus.company && (
                <ClearButton onClick={() => clearField(setCompany)}>
                  <ClearIcon src={IconCloseIcon} alt="clear company" />
                </ClearButton>
              )}
            </ModalEmailInput>
          </RequestInputBox>

          <Spacing pixel="8" />

          {/* ───────── Email ───────── */}
          <RequestInputBox inputValue={focus.email}>
            <DialogInputText inputValue={focus.email}>{labels.email[locale]}</DialogInputText>
            <ModalEmailInput focus={focus.email}>
              <DialogInput
                id="email-input"
                value={email}
                placeholder={placeholders.email[locale]}
                onChange={(e) => setEmail(e.target.value)}
                onClick={() => handleFocus('email')}
                onKeyDown={(e) => handleKeyDown(e, 'email')}
              />
              {email && focus.email && (
                <ClearButton onClick={() => clearField(setEmail)}>
                  <ClearIcon src={IconCloseIcon} alt="clear email" />
                </ClearButton>
              )}
            </ModalEmailInput>
          </RequestInputBox>

          <Spacing pixel="24" />

          {/* ───────── Policy & Checkbox ───────── */}
          <DialogPolicyBox>
            <DialogCheckBox src={checked ? IconCheckBoxBlack : IconCheckBoxEmpty} onClick={() => setChecked(!checked)} alt="checkbox" />

            {location.pathname.includes('/en/') ? (
              <>
                <PolicySpanText color="#000">
                  <Trans i18nKey="solutionCommon.modal.download.agree1" />{' '}
                </PolicySpanText>
                <PolicySpanText color="#000">
                  <Trans i18nKey="solutionCommon.modal.download.agree3" />{' '}
                </PolicySpanText>
                <StyledDownloadPolicyLink href="/terms/2" target="_blank">
                  <PolicySpanText color="#2A70F0">
                    <Trans i18nKey="solutionCommon.modal.download.agree2" />
                  </PolicySpanText>
                  <StyledUnderline />
                </StyledDownloadPolicyLink>
              </>
            ) : (
              <>
                <PolicySpanText color="#000">
                  <Trans i18nKey="solutionCommon.modal.download.agree1" />{' '}
                </PolicySpanText>
                <StyledDownloadPolicyLink href="/terms/2" target="_blank">
                  <PolicySpanText color="#2A70F0">
                    <Trans i18nKey="solutionCommon.modal.download.agree2" />
                  </PolicySpanText>
                  <StyledUnderline />
                </StyledDownloadPolicyLink>
                <PolicySpanText color="#000">
                  <Trans i18nKey="solutionCommon.modal.download.agree3" />{' '}
                </PolicySpanText>
              </>
            )}
          </DialogPolicyBox>

          <Spacing pixel="32" />

          {/* ───────── Buttons ───────── */}
          <DialogBtnBox>
            <DialogBtnCancel onClick={onClickToggleModal}>
              <StyledDownloadBtnlTextCancel size="medium" color="#000">
                <Trans i18nKey="solutionCommon.modal.download.cancel" />
              </StyledDownloadBtnlTextCancel>
            </DialogBtnCancel>

            <DialogBtn onClick={onDownload} bgcolor={submit ? '#000' : '#BDBDBD'}>
              <StyledDownloadBtnlTextSubmit size="medium" color="#FFF">
                <Trans i18nKey="solutionCommon.modal.download.confirm" />
              </StyledDownloadBtnlTextSubmit>
            </DialogBtn>
          </DialogBtnBox>
          <SpacingDTM d="0" t="0" m="96" />
        </DivW100Padding>
      </DialogBox>

      {/* ───────── Backdrop ───────── */}
      <Backdrop
        onClick={(e) => {
          e.preventDefault();
          onClickToggleModal();
        }}
      />
    </ModalContainer>
  );
}

export default ModalDone;
